import * as React from "react";
import * as ReactMarkdown from "react-markdown";
import * as classNames from "classnames";
import { WebPortalClient } from "webportalclient";
import { useDevicesStateContext } from '../DevicesStateContext';
import * as P230In2itiveApi from '../../misc/api/DeviceLinkApi/P230In2itive';
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { CloseInteractionModalAction, DevicesAction, SetDeviceOverallUploadStatusAction, SetInteractionModalStepAction, SetUploadStatusAction, UploadStatus } from "../DevicesNewReducer";
import { useWebPortalApiClient } from "../../misc/api/WebPortalTokenClient";
import { useAboutContextProvider } from "../../Core/AboutContextProvider";
import { useInterval } from "../../misc/Hooks/UseInterval";
import * as P230Messages from "../../misc/api/DeviceLinkApi/P230In2itiveMessages";
import { Upload } from "./Upload";
import { AuditTrailActionEnum } from "../../misc/api/ApiAuditActions";
import { uploadExistingSubjectData } from "../DiaryUploadHelpers";


interface DiaryResetSubjectConfigurationProps {
    dispatch: React.Dispatch<DevicesAction>;
}

export const DiaryResetSubjectConfiguration = ({ dispatch }: DiaryResetSubjectConfigurationProps) => {
    const client = useWebPortalApiClient();
    const { study } = useCurrentStudyConfigurationContext();
    const state = useDevicesStateContext();

    React.useEffect(() => {
        const resetSubjectConfiguration = async () => {
            dispatch(SetUploadStatusAction.create(P230Messages.UploadExistingData));
            let selectedDevice = await P230In2itiveApi.ReConnectToDevice();
            const deviceId = selectedDevice.id;

            try {
                const subjectResult = await P230In2itiveApi.GetSubject(deviceId);

                if (subjectResult) {
                    const uploadResult = await uploadExistingSubjectData(client.client,
                        study,
                        selectedDevice,
                        subjectResult.subject,
                        () => { },
                        (error) => dispatch(SetDeviceOverallUploadStatusAction.create(deviceId, UploadStatus.Failed, error)));

                    if (uploadResult === UploadStatus.Failed || uploadResult === UploadStatus.PartialFailed) {
                        await client.client.postAuditTrailEvent({
                            summary: `The In2itive eDiary '${selectedDevice.serial}' for subject '${subjectResult.subject.number}' has failed manual reset, with an upload error.`,
                            auditAction: AuditTrailActionEnum.UnsuccessfulManualResetIn2itiveDiary
                        });
                        dispatch(SetDeviceOverallUploadStatusAction.create(selectedDevice.id, UploadStatus.Failed, P230Messages.FailedToClear));

                        dispatch(SetInteractionModalStepAction.create(5));
                        return;
                    }
                }

                selectedDevice = await P230In2itiveApi.ReConnectToDevice();

                dispatch(SetUploadStatusAction.create(P230Messages.ClearingDevice));
                const clearResult = await P230In2itiveApi.ClearMemory(deviceId);
                if (!clearResult) {
                    await client.client.postAuditTrailEvent({
                        summary: subjectResult !== null && typeof subjectResult === "object" ? `The In2itive eDiary '${selectedDevice.serial}' for subject '${subjectResult.subject.number}' has failed manual reset, with a device clearing error.`
                            : `The In2itive eDiary '${selectedDevice.serial}' has failed been manual reset, with a device clearing error.`,
                        auditAction: AuditTrailActionEnum.UnsuccessfulManualResetIn2itiveDiary
                    });
                    dispatch(SetDeviceOverallUploadStatusAction.create(selectedDevice.id, UploadStatus.Failed, P230Messages.FailedToClear));

                    dispatch(SetInteractionModalStepAction.create(5));
                    return;
                }

                await P230In2itiveApi.RestartWithWait(deviceId);

                await P230In2itiveApi.ReConnectToDevice();

                dispatch(SetUploadStatusAction.create(P230Messages.ClearingDeploymentRecords));
                const currentDiaryPackageDeployments = await client.client.getDiaryPackageDeployments(undefined, `serialNumber eq '${selectedDevice.serial}'`);
                for (const deployment of currentDiaryPackageDeployments.value) {
                    await client.client.deleteDiaryPackageDeployment(deployment.id);
                }

                dispatch(SetUploadStatusAction.create(P230Messages.ResetComplete));
                await client.client.postAuditTrailEvent({
                    summary: subjectResult !== null && typeof subjectResult === "object" ? `The In2itive eDiary '${selectedDevice.serial}' for subject '${subjectResult?.subject?.number}' has been manually reset successfully.`
                        : `The In2itive eDiary '${selectedDevice.serial}' has been manually reset successfully.`,
                    auditAction: AuditTrailActionEnum.SuccessfulManualResetIn2itiveDiary
                });
                dispatch(SetDeviceOverallUploadStatusAction.create(selectedDevice.id, UploadStatus.Uploaded, P230Messages.ResetComplete));

                dispatch(SetInteractionModalStepAction.create(4));
            } catch {
                await client.client.postAuditTrailEvent({
                    summary: `The In2itive eDiary '${selectedDevice.serial}' has failed manual reset.`,
                    auditAction: AuditTrailActionEnum.UnsuccessfulManualResetIn2itiveDiary
                });
                dispatch(SetDeviceOverallUploadStatusAction.create(selectedDevice.id, UploadStatus.Failed, P230Messages.FailedToClear));

                dispatch(SetInteractionModalStepAction.create(5));
            }
        }

        resetSubjectConfiguration();
    }, []);

    const handleClose = () => {
        dispatch(CloseInteractionModalAction.create());
    }

    const failedStatus = state.upload.deviceStatus.filter(s => s.status === UploadStatus.PartialFailed || s.status === UploadStatus.Failed).length > 0;

    const statusClasses = classNames("fas", {
        "fa-circle-notch": !failedStatus,
        "fa-spin": !failedStatus,
        "fa-times-circle": failedStatus,
        "text-danger": failedStatus
    });

    return (
        <div>
            <div className="modal-header bg-primary">
                <h3 className="modal-title">Resetting Device</h3>
            </div>
            <div className="modal-body">
                <p style={{ fontSize: 100 + 'px' }} className="text-center text-info">
                    <i id="ConfigureStatus" className={statusClasses} aria-hidden="true"></i>
                </p>
                <p id="progress" className="text-center">
                    <ReactMarkdown>
                        {state.currentStatusText}
                    </ReactMarkdown>
                </p>
            </div>
            {failedStatus ? <div className="modal-footer">
                <button className="btn btn-primary" onClick={handleClose}>Finish</button>
            </div> : null}
        </div>
    );
}
